import React, { Fragment, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Components

import Logo from '@common-components/headers/fragments/Logo';
import Header from '@common-components/headers/header';
import TopNavHeader from '@common-components/headers/TopNavHeader';
import Impersonation from '../individualPortal/Impersonation';

// ActionCreators

import { changeLang, showModal } from '@actions/common/actionCreators';
import { fetchUnreadNotificationCount } from '@actions/individualPortal/secureInbox/secureInboxActionCreators';

// Utils

import { isFormInInitialState } from '@reducers/appReducer';

// Hooks

import { useInboxId } from '@hooks/useInboxId';
import { useMobileNavFocus } from '@hooks/useMobileNavFocus';
import { useTitle } from '@hooks/useTitle';
import { useTrapFocus } from '@hooks/useTrapFocus';

// Constants
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { allowedRoutes } from '@constants/individualPortal/capAlerts/capAlerts.constants';
import { ALL_USER_ROLES } from '@constants/roles/userRoleTypes';
import CapAlertContainer from '@containers/individualPortal/capAlerts/CapAlertContainer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from '@hooks/useLocation';

const HeaderContainer = () => {
    const dispatch = useDispatch();
    const environment = useSelector(state => state.common.environment);
    const loadedLanguages = useSelector(state => state.common.messages?.loadedLanguages ?? []);
    const errorStatus = useSelector(state => state.common.error?.errorStatus);
    const adjustedFlow = useSelector(state => state.ssap.adjustedFlow);
    const page = useSelector(state => state.ssap.page);
    const ssapForm = useSelector(isFormInInitialState);
    const { currentLocation } = useLocation();

    const { setNavRef, navRef, isOpen: isMenuOpen } = useMobileNavFocus();
    useTrapFocus({ modalRef: navRef, enabled: isMenuOpen });

    const user = environment?.user ?? {};
    const { activeModuleId: householdId = 0, role, roles, userId } = user;

    const inboxIdData = {
        householdId,
        impersonation: environment?.impersonation ?? false,
        impersonationDetails: environment?.impersonationDetails,
        telexEnabled: environment?.telexEnabled,
        role,
        userId,
    };
    const { part, inboxId } = useInboxId(inboxIdData);

    const headerProfileKey = environment ? environment.profile ?? environment.stateCode : '';

    useTitle('', {
        adjustedFlow,
        exchangeName: environment?.exchangeName ?? '',
        language: environment?.language ?? 'en',
        loadedLanguages,
        page,
        pathname: currentLocation,
        stateCode: headerProfileKey,
    });

    useEffect(() => {
        if (environment?.telexEnabled && environment?.user) {
            dispatch(fetchUnreadNotificationCount(part, inboxId));
        }
    }, [dispatch, environment?.telexEnabled, environment?.user, part, inboxId]);

    const isCapAlertVisible = useMemo(
        () =>
            Boolean(environment?.capAlertsVisible) &&
            allowedRoutes.some(route => window.location.pathname.includes(route)),
        [environment?.capAlertsVisible, currentLocation],
    );

    if (!environment) {
        return null;
    }

    const {
        isMNFullStack,
        language,
        profile,
        stateCode,
        impersonation,
        impersonationDetails,
        assets: { LOGO: logo, LOGO_MOBILE: logoMobile },
        links: { LOGO_URL: logoUrlLink, DASHBOARD: dashboardUrlLink },
    } = environment;

    const { name, logoUrl: carrierLogoUrl, logoUrlMobile, micrositeIds } = environment.affiliate || {};

    const headerProfileKeyResolved = profile ?? stateCode;

    const currentPath = window.location.pathname;

    const headerProps = {
        environment,
        language,
        changeLang: (...args) => dispatch(changeLang(...args)),
        showModal: (...args) => dispatch(showModal(...args)),
        currentPath,
        errorStatus,
        ssapForm,
    };

    const isShowTopNav = roles && Object.keys(roles).length > 1 && role != ALL_USER_ROLES.INDIVIDUAL;
    const topNavHeaderProps = { environment, hasSubMenu: false, isTopNav: true, stateCode: headerProfileKeyResolved };

    return (
        <Fragment>
            <a className="usa-skipnav gi-skipnav" href="#main-content">
                Skip to Main Content
            </a>
            {isShowTopNav ? <TopNavHeader {...topNavHeaderProps} /> : null}

            <div className="main-nav-masthead main-nav-submenu">
                <header
                    className="usa-header usa-header--basic gi-masthead"
                    id={`masthead_${headerProfileKeyResolved}`}
                    role="banner"
                >
                    <div className="usa-nav-container gi-masthead__container">
                        <div className="usa-navbar gi-navbar">
                            <Logo
                                {...{
                                    logo,
                                    logoUrlLink,
                                    dashboardUrlLink,
                                    logoMobile,
                                    stateCode: headerProfileKeyResolved,
                                    isMNFullStack,
                                    name,
                                    carrierLogoUrl,
                                    logoUrlMobile,
                                    micrositeIds,
                                    hasUser: !!environment.user,
                                    isAnonymous: environment.anonymous,
                                }}
                            />
                        </div>
                        <nav ref={setNavRef} role="navigation" className="usa-nav gi-main__nav">
                            <button aria-label="Close" className="usa-nav__close gi-main__nav__close">
                                <FontAwesomeIcon icon="times" />
                            </button>
                            <Header {...headerProps} />
                        </nav>
                    </div>
                </header>
                <Impersonation
                    {...{
                        impersonation,
                        impersonationDetails,
                        changeLang: (...args) => dispatch(changeLang(...args)),
                    }}
                />
                <ConditionalRendering isTrue={isCapAlertVisible}>
                    <CapAlertContainer />
                </ConditionalRendering>
            </div>
        </Fragment>
    );
};

export default HeaderContainer;
